/**
 * Custom hook for rendering a D3 hexbin shot chart.
 * Handles:
 * - Hexbin computation from filtered shots
 * - SVG rendering with enter/update/exit pattern
 * - Smooth animated transitions when filters change
 * - Hover interactions for tooltips
 */

import { useEffect, useRef, useMemo, useCallback } from "react";
import * as d3 from "d3";
import { hexbin as d3Hexbin, HexbinBin } from "d3-hexbin";
import {
  Shot,
  ShotFilters,
  HexbinData,
  COURT_CONSTANTS,
} from "./types";
import {
  applyFilters,
  colorScale,
  createSizeScale,
  drawCourt,
  createCoordinateTransformer,
  mergeAdjacentBins,
} from "./utils";

// Animation constants
const TRANSITION_DURATION = 300;
const HOVER_TRANSITION_DURATION = 100;

// Outline styles
const HOVER_STROKE_COLOR = "#000";
const HOVER_STROKE_WIDTH = 2;
const SELECTED_STROKE_COLOR = "#616161"; // Dark gray to match SelectedHexbinStats
const SELECTED_STROKE_WIDTH = 3;

interface UseHexbinChartOptions {
  width: number;
  height: number;
  hexagonRadius?: number;
  showCollegeLine?: boolean;
  onHexagonHover?: (data: HexbinData | null, event: MouseEvent) => void;
  onHexagonClick?: (data: HexbinData) => void;
  selectedHexKey?: string | null;
}

interface UseHexbinChartReturn {
  svgRef: React.RefObject<SVGSVGElement>;
  filteredShotCount: number;
  hexbinData: HexbinData[];
}

/**
 * Hook to manage a D3 hexbin chart visualization.
 *
 * @param shots - Array of shot data to visualize
 * @param filters - Current filter state
 * @param options - Chart configuration options
 * @returns Object containing SVG ref and filtered shot count
 */
export function useHexbinChart(
  shots: Shot[],
  filters: ShotFilters,
  options: UseHexbinChartOptions
): UseHexbinChartReturn {
  const {
    width,
    height,
    hexagonRadius = COURT_CONSTANTS.HEXAGON_RADIUS,
    showCollegeLine = false,
    onHexagonHover,
    onHexagonClick,
    selectedHexKey,
  } = options;

  const svgRef = useRef<SVGSVGElement>(null);
  const isInitializedRef = useRef(false);

  // Memoize filtered shots to avoid unnecessary recomputations
  const filteredShots = useMemo(
    () => applyFilters(shots, filters),
    [shots, filters]
  );

  // Exclude shots without location data from hexbin chart (but keep them in filteredShotCount for metrics)
  const plottableShots = useMemo(
    () => filteredShots.filter((s) => s.x != null && s.y != null),
    [filteredShots]
  );

  // Compute coordinate transformer
  const transformer = useMemo(
    () => createCoordinateTransformer(width, height),
    [width, height]
  );

  // Create hexbin generator
  const hexbin = useMemo(() => {
    const scaledRadius = hexagonRadius * transformer.scaleX;
    return d3Hexbin<Shot>()
      .x((d: Shot) => transformer.translateX(d.x))
      .y((d: Shot) => transformer.translateY(d.y))
      .radius(scaledRadius)
      .extent([
        [0, 0],
        [width, height],
      ]);
  }, [width, height, hexagonRadius, transformer]);

  // Compute hexbin data with adjacent bin merging
  const hexbinData: HexbinData[] = useMemo(() => {
    if (plottableShots.length === 0) return [];

    const bins: HexbinBin<Shot>[] = hexbin(plottableShots);

    // First pass: each bin's own stats; then the shared adjacent-bin merge (mergeAdjacentBins, also the report
    // charts') adds its neighbours' for colour, size and the hover line
    const binsWithStats = mergeAdjacentBins(
      bins.map((bin) => {
        const makes = bin.filter((s: Shot) => s.made).length;
        const missedNotFouled = bin.filter((s: Shot) => !s.made && !s.fouled).length;
        const fga = makes + missedNotFouled;
        const attempts = bin.length;
        const ptsScored = bin.reduce((sum: number, s: Shot) => sum + s.ptsScored, 0);
        return {
          x: bin.x,
          y: bin.y,
          shots: [...bin],
          makes,
          attempts,
          ptsScored,
          fgPct: fga > 0 ? makes / fga : 0,
          // TS% = PTS / attempts / 2
          tsPct: attempts > 0 ? ptsScored / attempts / 2 : 0,
        };
      }),
      hexagonRadius * transformer.scaleX,
    );

    return binsWithStats;
  }, [plottableShots, hexbin, hexagonRadius, transformer.scaleX]);

  // Create size scale based on merged attempts (for visual density)
  // Pass full bin distribution so the scale can compute percentile-based max
  const sizeScale = useMemo(() => {
    const binAttempts = hexbinData.map((d) => d.mergedAttempts);
    return createSizeScale(binAttempts, plottableShots.length);
  }, [hexbinData, plottableShots.length]);

  // Hexagon path generator
  const hexagonPath = useCallback(
    (radius: number) => hexbin.hexagon(radius),
    [hexbin]
  );

  // Scaled hexagon radius
  const scaledRadius = hexagonRadius * transformer.scaleX;

  // Get selected hex keys (selected hex + its neighbors)
  const selectedKeys = useMemo(() => {
    if (!selectedHexKey) return new Set<string>();
    const selectedHex = hexbinData.find((d) => d.key === selectedHexKey);
    if (!selectedHex) return new Set<string>();
    return new Set([selectedHex.key, ...selectedHex.neighborKeys]);
  }, [selectedHexKey, hexbinData]);

  // Use refs to always have access to current values in D3 event handlers
  // D3 event handlers capture closures at attachment time and don't update with React re-renders
  const selectedKeysRef = useRef<Set<string>>(selectedKeys);
  selectedKeysRef.current = selectedKeys;

  const selectedHexKeyRef = useRef<string | null | undefined>(selectedHexKey);
  selectedHexKeyRef.current = selectedHexKey;

  // Store callbacks in refs so D3 handlers always call the latest version
  const onHexagonClickRef = useRef(onHexagonClick);
  onHexagonClickRef.current = onHexagonClick;

  const onHexagonHoverRef = useRef(onHexagonHover);
  onHexagonHoverRef.current = onHexagonHover;

  // Mouse event handlers - highlight hovered hex + all adjacent hexes
  // Note: These handlers use refs to always get the latest values,
  // since D3 event handlers are attached once and don't update with React re-renders
  const handleMouseEnter = useCallback(
    (event: MouseEvent, d: HexbinData) => {
      if (!svgRef.current) return;

      const svg = d3.select(svgRef.current);
      const currentSelectedKeys = selectedKeysRef.current;

      // Get all keys to highlight for hover (current + neighbors)
      const hoverKeys = new Set([d.key, ...d.neighborKeys]);

      // Highlight all hexagons - different style for hover vs selected
      svg.selectAll(".hexagon").each(function () {
        const hexElement = d3.select(this);
        const hexKey = hexElement.attr("data-key");
        const isHovered = hoverKeys.has(hexKey);
        const isSelected = currentSelectedKeys.has(hexKey);

        if (isHovered) {
          // Hover style takes precedence visually
          hexElement
            .transition()
            .duration(HOVER_TRANSITION_DURATION)
            .attr("stroke", HOVER_STROKE_COLOR)
            .attr("stroke-width", HOVER_STROKE_WIDTH);
        } else if (isSelected) {
          // Keep selected style for non-hovered selected hexes
          hexElement
            .attr("stroke", SELECTED_STROKE_COLOR)
            .attr("stroke-width", SELECTED_STROKE_WIDTH);
        }
      });

      onHexagonHoverRef.current?.(d, event);
    },
    []
  );

  const handleMouseLeave = useCallback(
    (event: MouseEvent) => {
      if (!svgRef.current) return;

      const svg = d3.select(svgRef.current);
      const currentSelectedKeys = selectedKeysRef.current;

      // Restore appropriate state for all hexagons
      svg.selectAll(".hexagon").each(function () {
        const hexElement = d3.select(this);
        const hexKey = hexElement.attr("data-key");
        const isSelected = currentSelectedKeys.has(hexKey);

        if (isSelected) {
          // Restore selected style
          hexElement
            .transition()
            .duration(HOVER_TRANSITION_DURATION)
            .attr("stroke", SELECTED_STROKE_COLOR)
            .attr("stroke-width", SELECTED_STROKE_WIDTH);
        } else {
          // Remove highlight
          hexElement
            .transition()
            .duration(HOVER_TRANSITION_DURATION)
            .attr("stroke", "none")
            .attr("stroke-width", 0);
        }
      });

      onHexagonHoverRef.current?.(null, event);
    },
    []
  );

  // Click handler for selection - uses ref to get latest selected key for toggle logic
  const handleClick = useCallback(
    (event: MouseEvent, d: HexbinData) => {
      onHexagonClickRef.current?.(d);
    },
    []
  );

  // Initialize chart (run once)
  useEffect(() => {
    if (!svgRef.current || isInitializedRef.current) return;

    const svg = d3.select(svgRef.current);

    // Set up SVG dimensions
    svg
      .attr("width", width)
      .attr("height", height)
      .attr("viewBox", `0 0 ${width} ${height}`);

    // Create groups for court and hexagons
    svg.append("g").attr("class", "court-group");
    svg.append("g").attr("class", "hexagon-group");

    // Draw court
    const courtGroup = svg.select(".court-group");
    drawCourt(courtGroup, { width, height, showCollegeLine });

    isInitializedRef.current = true;
  }, [width, height, showCollegeLine]);

  // Update hexagons when data or filters change
  useEffect(() => {
    if (!svgRef.current || !isInitializedRef.current) return;

    const svg = d3.select(svgRef.current);
    const hexagonGroup = svg.select(".hexagon-group");

    // Key function for data join - use position as stable identifier
    const keyFn = (d: HexbinData) => `${Math.round(d.x)}-${Math.round(d.y)}`;

    // Select existing hexagons and bind data
    const hexagons = hexagonGroup
      .selectAll(".hexagon")
      .data(hexbinData, keyFn as any);

    // EXIT: Remove hexagons that are no longer in data
    hexagons
      .exit()
      .transition()
      .duration(TRANSITION_DURATION)
      .attr("d", hexagonPath(0))
      .style("opacity", 0)
      .remove();

    // ENTER: Create new hexagons
    const hexagonsEnter = hexagons
      .enter()
      .append("path")
      .attr("class", "hexagon")
      .attr("data-key", (d: HexbinData) => d.key) // Store key for hover/selection highlighting
      .attr("transform", (d: HexbinData) => `translate(${d.x}, ${d.y})`)
      .attr("d", hexagonPath(0)) // Start at radius 0
      .style("fill", (d: HexbinData) => colorScale(d.mergedTsPct)) // Use merged TS% for coloring
      .style("opacity", 0)
      .style("cursor", "pointer")
      .on("mouseenter", function (event: MouseEvent, d: HexbinData) {
        handleMouseEnter(event, d);
      })
      .on("mouseleave", function (event: MouseEvent) {
        handleMouseLeave(event);
      })
      .on("click", function (event: MouseEvent, d: HexbinData) {
        handleClick(event, d);
      });

    // Animate entering hexagons - use mergedAttempts for sizing
    hexagonsEnter
      .transition()
      .duration(TRANSITION_DURATION)
      .attr("d", (d: HexbinData) =>
        hexagonPath(sizeScale(d.mergedAttempts) * scaledRadius)
      )
      .style("opacity", 0.85);

    // UPDATE: Animate existing hexagons - use mergedAttempts for sizing
    hexagons
      .transition()
      .duration(TRANSITION_DURATION)
      .attr("d", (d: HexbinData) =>
        hexagonPath(sizeScale(d.mergedAttempts) * scaledRadius)
      )
      .style("fill", (d: HexbinData) => colorScale(d.mergedTsPct)) // Use merged TS% for coloring
      .style("opacity", 0.85);
  }, [
    hexbinData,
    hexagonPath,
    sizeScale,
    scaledRadius,
    handleMouseEnter,
    handleMouseLeave,
    handleClick,
  ]);

  // Update selection highlighting when selectedHexKey changes
  useEffect(() => {
    if (!svgRef.current || !isInitializedRef.current) return;

    const svg = d3.select(svgRef.current);

    // Update all hexagons to show/hide selection state
    svg.selectAll(".hexagon").each(function () {
      const hexElement = d3.select(this);
      const hexKey = hexElement.attr("data-key");
      const isSelected = selectedKeys.has(hexKey);

      if (isSelected) {
        hexElement
          .attr("stroke", SELECTED_STROKE_COLOR)
          .attr("stroke-width", SELECTED_STROKE_WIDTH);
      } else {
        hexElement.attr("stroke", "none").attr("stroke-width", 0);
      }
    });
  }, [selectedKeys]);

  // Cleanup on unmount — remove D3 elements and event listeners
  useEffect(() => {
    const svg = svgRef.current;
    return () => {
      if (svg) d3.select(svg).selectAll("*").remove();
      isInitializedRef.current = false;
    };
  }, []);

  return {
    svgRef,
    filteredShotCount: filteredShots.length,
    hexbinData,
  };
}

export default useHexbinChart;
